/**
 * Complete Production-Grade Customer Management & CRM Route — Module 6.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { siteConfig } from "@/config/site";
import {
  Users,
  Eye,
  Lock,
  Unlock,
  ShieldAlert,
  UserCheck,
  CheckCircle2,
  IndianRupee,
  Check,
  Download,
  Plus,
} from "lucide-react";
import { formatPrice } from "@/components/forms/price-display";
import { DataTable, type ColumnDef } from "@/components/admin/common/data-table";
import {
  fetchAdminCustomersFromSupabase,
  getStoredAdminCustomers,
  toggleCustomerBlockInStore,
  addCustomerStaffNoteInStore,
  type CustomerRecord,
  type CustomerTier,
} from "@/features/admin/customers/store/admin-customers-store";
import { CustomerDetailDrawer } from "@/components/admin/customers/customer-detail-drawer";

export const Route = createFileRoute("/admin/customers")({
  head: () => ({
    meta: [{ title: `Customer Management — Admin — ${siteConfig.name}` }],
  }),
  component: AdminCustomersPage,
});

function AdminCustomersPage() {
  const [customers, setCustomers] = useState<CustomerRecord[]>(() => getStoredAdminCustomers());
  const [tierFilter, setTierFilter] = useState<string>("ALL");

  // Drawer State
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerRecord | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const loadData = () => {
    fetchAdminCustomersFromSupabase()
      .then((list) => {
        if (list && list.length > 0) {
          setCustomers(list);
        }
      })
      .catch(() => {
        setCustomers(getStoredAdminCustomers());
      });
  };

  useEffect(() => {
    loadData();

    const handleSync = () => {
      loadData();
    };

    window.addEventListener("customers_updated", handleSync);
    window.addEventListener("orders_updated", handleSync);
    window.addEventListener("storage", handleSync);

    return () => {
      window.removeEventListener("customers_updated", handleSync);
      window.removeEventListener("orders_updated", handleSync);
      window.removeEventListener("storage", handleSync);
    };
  }, []);

  const triggerToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const handleOpenDetail = (cust: CustomerRecord, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedCustomer(cust);
    setDetailDrawerOpen(true);
  };

  const handleToggleBlock = async (customerId: string) => {
    const target = customers.find((c) => c.id === customerId);
    const shouldBlock = target ? !target.isBlocked : true;
    try {
      const updated = await toggleCustomerBlockInStore(customerId, shouldBlock);
      setCustomers(updated);
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer(updated.find((c: CustomerRecord) => c.id === customerId) || null);
      }
      triggerToast("Customer account status updated.");
    } catch (err: unknown) {
      console.error("Failed to update customer status:", err);
      const message = (err as { message?: string }).message;
      triggerToast(`Failed to update customer in database: ${message || "Database error"}`);
    }
  };

  const handleAddNote = (customerId: string, text: string) => {
    const updated = addCustomerStaffNoteInStore(customerId, text);
    setCustomers(updated);
    if (selectedCustomer?.id === customerId) {
      setSelectedCustomer(updated.find((c) => c.id === customerId) || null);
    }
    triggerToast("CRM staff note recorded.");
  };

  // Filtered Customers List
  const filteredCustomers = useMemo(() => {
    if (tierFilter === "ALL") return customers;
    return customers.filter((c) => c.tier === tierFilter);
  }, [customers, tierFilter]);

  // Statistics Summary Metrics
  const stats = useMemo(() => {
    const totalCount = customers.length;
    const repeatCount = customers.filter((c) => c.tier === "REPEAT").length;
    const newCount = customers.filter((c) => c.tier === "NEW").length;
    const blockedCount = customers.filter((c) => c.isBlocked).length;
    const totalRevenue = customers.reduce((acc, c) => acc + c.totalSpent, 0);
    const avgLtv = totalCount > 0 ? totalRevenue / totalCount : 0;

    return { totalCount, repeatCount, newCount, blockedCount, avgLtv };
  }, [customers]);

  // DataTable Columns
  const columns: ColumnDef<CustomerRecord>[] = [
    {
      id: "name",
      header: "Customer Name & Contact",
      accessorKey: "name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center font-display font-bold text-primary text-xs shrink-0">
            {row.name[0]}
          </div>
          <div>
            <p className="font-semibold text-foreground text-xs">{row.name}</p>
            <p className="text-[11px] text-muted-foreground font-mono">{row.email}</p>
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      id: "phone",
      header: "Phone",
      accessorKey: "phone",
      cell: (row) => <span className="font-mono text-xs text-muted-foreground">{row.phone}</span>,
    },
    {
      id: "tier",
      header: "Customer Tier",
      accessorKey: "tier",
      cell: (row) => (
        <span
          className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
            row.tier === "BLOCKED"
              ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
              : row.tier === "REPEAT"
                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                : "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300"
          }`}
        >
          {row.tier}
        </span>
      ),
      sortable: true,
    },
    {
      id: "ordersCount",
      header: "Orders Placed",
      accessorKey: "ordersCount",
      cell: (row) => (
        <span className="font-mono text-xs font-semibold">{row.ordersCount} orders</span>
      ),
      sortable: true,
    },
    {
      id: "totalSpent",
      header: "Lifetime Value (LTV)",
      accessorKey: "totalSpent",
      cell: (row) => (
        <span className="font-display font-bold text-xs tabular-nums text-foreground">
          {formatPrice(row.totalSpent)}
        </span>
      ),
      sortable: true,
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={(e) => handleOpenDetail(row, e)}
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-secondary cursor-pointer"
            title="View CRM Telemetry & History"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggleBlock(row.id);
            }}
            className={`p-1.5 rounded transition-colors cursor-pointer ${
              row.isBlocked
                ? "text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                : "text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"
            }`}
            title={row.isBlocked ? "Unblock Customer" : "Block Customer"}
          >
            {row.isBlocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-16">
      {/* Toast Notification */}
      {toastMsg && (
        <div className="fixed top-20 right-6 z-50 bg-foreground text-background text-xs font-semibold px-4 py-3 rounded-md shadow-xl flex items-center gap-2 animate-in fade-in duration-150">
          <Check className="h-4 w-4 text-emerald-400" />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs text-primary font-semibold">
            Customer Relationship Management
          </p>
          <h1 className="mt-1 font-display text-3xl text-foreground">Customer Management & CRM</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Track customer lifetime value (LTV), view order history, saved addresses, wishlist
            items, and manage account suspensions.
          </p>
        </div>
      </div>

      {/* Summary Metric Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">Total Customers</span>
          <span className="font-display text-xl font-bold text-foreground">{stats.totalCount}</span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">Repeat Buyers</span>
          <span className="font-display text-xl font-bold text-emerald-700 dark:text-emerald-400">
            {stats.repeatCount}
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">New Customers</span>
          <span className="font-display text-xl font-bold text-blue-700 dark:text-blue-400">
            {stats.newCount}
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">Blocked Accounts</span>
          <span className="font-display text-xl font-bold text-rose-700 dark:text-rose-400">
            {stats.blockedCount}
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Average Customer LTV
          </span>
          <span className="font-display text-lg font-bold text-foreground">
            {formatPrice(stats.avgLtv)}
          </span>
        </div>
      </div>

      {/* Tier Filter Tabs */}
      <div className="flex items-center gap-1 border-b border-border pb-3 text-xs uppercase tracking-wider overflow-x-auto">
        {["ALL", "REPEAT", "NEW", "BLOCKED"].map((tier) => (
          <button
            key={tier}
            onClick={() => setTierFilter(tier)}
            className={`px-3 py-1.5 rounded transition-[color,background-color,border-color,box-shadow,transform,opacity] font-medium cursor-pointer ${
              tierFilter === tier
                ? "bg-primary text-primary-foreground font-semibold shadow-2xs"
                : "bg-card border border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {tier} (
            {tier === "ALL" ? customers.length : customers.filter((c) => c.tier === tier).length})
          </button>
        ))}
      </div>

      {/* Enterprise Data Table */}
      <DataTable
        data={filteredCustomers}
        columns={columns}
        keyExtractor={(row) => row.id}
        searchPlaceholder="Search customer by name, email, or phone number..."
        searchField={(row) => `${row.name} ${row.email} ${row.phone}`}
        onRowClick={(row) => handleOpenDetail(row)}
        bulkActions={[
          {
            label: "Bulk Suspend Selected",
            variant: "destructive",
            onClick: (selected) => {
              selected.forEach((c) => toggleCustomerBlockInStore(c.id, true, "Bulk suspension"));
              loadData();
              triggerToast(`Suspended ${selected.length} customer accounts.`);
            },
          },
        ]}
      />

      {/* Customer CRM Drawer */}
      <CustomerDetailDrawer
        open={detailDrawerOpen}
        customer={selectedCustomer}
        onClose={() => {
          setDetailDrawerOpen(false);
          setSelectedCustomer(null);
        }}
        onToggleBlock={handleToggleBlock}
        onAddNote={handleAddNote}
      />
    </div>
  );
}
